/* eslint-disable camelcase */
/* eslint-disable import/no-named-as-default */

// node modules
import React from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';

// app modules: components
import Button from 'components/Button';
import ContentTile from 'components/ContentTile';
import HeadContent from 'components/HeadContent';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link from 'components/Link';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// app modules: services
import { isApp } from 'services/environment';
import route from 'services/route';

// graphql
import ContentTileFields from 'components/ContentTile/ContentTileFields.graphql';

// assets
import CssTopRecipes from './TopRecipes.scss';
import { COLOURS } from '../../constants';

const QUERY = gql`
  query GetTopContent(
    $personId: String
    $isbn: String
    $orderBy: RecipeSortOrder
    $orderByDirection: SortOrderDirection
    $offset: Int
    $limit: Int
    $includeTags: [String]
    $includeAlacarte: Boolean
  ) {
    Content(
      offset: $offset
      limit: $limit
      orderBy: $orderBy
      orderByDirection: $orderByDirection
      personId: $personId
      isbn: $isbn
      includeTags: $includeTags
      includeAlacarte: $includeAlacarte
    ) {
      ...ContentTileFields
    }
  }
  ${ContentTileFields}
`;

function TopRecipes(props) {
  const {
    className,
    forceSsr,
    hideHeading,
    isbn,
    onViewMore,
    personId,
    showOwnership,
    showViewAll,
    viewMoreHref,
  } = props;
  useStyles(CssTopRecipes);
  const baseUrl = useSelector(state => state.config.baseUrl);
  const includeTags = useSelector(state =>
    state.user.oven_brands?.map(brand => brand.toLowerCase()),
  );
  const userLoaded = useSelector(state => state.user.logged_in === false || state.user.id > 0);
  const [alacarteEnabled] = useFeatureToggle('alacarte');

  let skip = !userLoaded;
  if (forceSsr) {
    skip = false;
  }
  const { data, loading } = useQuery(QUERY, {
    variables: {
      orderBy: 'rank',
      orderByDirection: 'desc',
      personId,
      isbn,
      limit: 10,
      offset: 0,
      includeTags,
      includeAlacarte: alacarteEnabled,
    },
    // No SSR because we don't know which tags are going to be requested until the user loads.
    // But if its google then lets SSR it anyway.
    ssr: forceSsr,
    skip,
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
  });
  const recipes = data?.Content;
  if (recipes?.length > 0) {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: recipes.map((recipe, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${baseUrl}${route.content(recipe.id, recipe.title)}`,
      })),
    };
    return (
      <div className={`top-recipes ${className}`}>
        <HeadContent
          contents={{
            structuredData,
          }}
        />
        {hideHeading ? null : (
          <Heading withDash>
            <Link to={viewMoreHref} onClick={onViewMore}>
              Most popular
            </Link>
          </Heading>
        )}
        <div className="top-recipes__content">
          {recipes
            .slice(0, 5)
            .map(recipe => (
              <ContentTile
                key={recipe.id}
                view="list"
                item={recipe}
                referrer="Book"
                section="Books"
                showOwnership={showOwnership}
              />
            ))}
        </div>
        {showViewAll ? (
          <div className="top-recipes__footer">
            <Button
              href={viewMoreHref}
              onClick={onViewMore}
              themeType="clear"
              themeName="energized"
              size="large"
            >
              <Icon fill={COLOURS.ENERGIZED} name="grid" height="14" width="14" mr={5} />
              View All Recipes
            </Button>
          </div>
        ) : null}
      </div>
    );
  } else if (loading || !userLoaded) {
    return (
      <div className="top-recipes">
        <Heading withDash>Most popular</Heading>
        <div className="top-recipes__content">
          {[1, 2, 3, 4, 5].map(skeleton => <Skeleton key={skeleton} width="100%" height={120} />)}
        </div>
        {showViewAll ? (
          <div className="top-recipes__footer">
            <Button onClick={onViewMore} themeType="clear" themeName="energized" size="large">
              <Icon fill={COLOURS.ENERGIZED} name="grid" height="14" width="14" mr={5} />
              View All Recipes
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
  return null;
}

TopRecipes.propTypes = {
  className: PropTypes.string,
  forceSsr: PropTypes.bool,
  hideHeading: PropTypes.bool,
  isbn: PropTypes.string,
  onViewMore: PropTypes.func,
  personId: PropTypes.string,
  showOwnership: PropTypes.bool,
  showViewAll: PropTypes.bool,
  viewMoreHref: PropTypes.string,
};

TopRecipes.defaultProps = {
  className: '',
  forceSsr: false,
  hideHeading: false,
  onViewMore: () => {},
  isbn: null,
  personId: null,
  showViewAll: true,
  showOwnership: false,
  viewMoreHref: null,
};

export default TopRecipes;
